/**
 * Vector + easing maths for the skeleton runtime (docs/SKEL-EDITOR.md §5.1 `math.ts`).
 *
 * Everything here works in SkelForm's world convention: y up, radians counter-clockwise
 * (report 01 §3). `interpolate` is the rusty_skelform 0.8 curve solver re-implemented clean-room
 * (report 01 §6.3.1): a CSS-style cubic bezier with P0 = (0,0) and P3 = (1,1) whose two handles
 * live on the DESTINATION key, solved for x with 5 Newton steps; a handle y of 999 on both = snap.
 * Frames may be fractional here (rusty truncates to u32), which is what makes 24 fps clips play
 * smoothly at 60 Hz (report 01 §6.2 recommendation).
 */
import { HANDLES, type HandlePreset, type V2 } from './types'

export function v2(x: number, y: number): V2 {
  return { x, y }
}

/** `R(a)·v` — rotate counter-clockwise by `a` radians (y-up). */
export function rot(v: V2, a: number): V2 {
  const c = Math.cos(a)
  const s = Math.sin(a)
  return { x: v.x * c - v.y * s, y: v.x * s + v.y * c }
}

export function add(a: V2, b: V2): V2 {
  return { x: a.x + b.x, y: a.y + b.y }
}

export function sub(a: V2, b: V2): V2 {
  return { x: a.x - b.x, y: a.y - b.y }
}

/** Uniform scale by a scalar. */
export function scale2(v: V2, s: number): V2 {
  return { x: v.x * s, y: v.y * s }
}

/** Component-wise product (SkelForm's `⊙`). */
export function mul2(a: V2, b: V2): V2 {
  return { x: a.x * b.x, y: a.y * b.y }
}

/** Component-wise division; a zero divisor yields 0 for that component instead of Infinity/NaN. */
export function div2(a: V2, b: V2): V2 {
  return { x: b.x === 0 ? 0 : a.x / b.x, y: b.y === 0 ? 0 : a.y / b.y }
}

export function dot(a: V2, b: V2): number {
  return a.x * b.x + a.y * b.y
}

/** z of the 3D cross product (a.x·b.y − a.y·b.x): > 0 when b is counter-clockwise of a. */
export function cross(a: V2, b: V2): number {
  return a.x * b.y - a.y * b.x
}

export function len(v: V2): number {
  return Math.hypot(v.x, v.y)
}

export function dist(a: V2, b: V2): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

/** Unit vector; the zero vector stays (0,0) (rusty `normalize`). */
export function norm(v: V2): V2 {
  const m = Math.hypot(v.x, v.y)
  return m === 0 ? { x: 0, y: 0 } : { x: v.x / m, y: v.y / m }
}

/** `atan2(v.y, v.x)` — the direction of `v` in radians (y-up, CCW). */
export function angleOf(v: V2): number {
  return Math.atan2(v.y, v.x)
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

export function lerp2(a: V2, b: V2, t: number): V2 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

const TAU = Math.PI * 2

/** Wrap an angle into (−π, π]. */
export function wrapAngle(a: number): number {
  if (!Number.isFinite(a)) return a
  let r = a % TAU
  if (r <= -Math.PI) r += TAU
  else if (r > Math.PI) r -= TAU
  return r
}

/** Signed shortest rotation from `a` to `b`, in (−π, π] (SkelForm `shortest_angle_delta`). */
export function shortestDelta(a: number, b: number): number {
  return wrapAngle(b - a)
}

/** One coordinate of the cubic bezier with P0 = 0, P3 = 1 and control values p1, p2. */
export function bezierAt(t: number, p1: number, p2: number): number {
  const u = 1 - t
  return 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t
}

/** d/dt of `bezierAt`. */
export function bezierDeriv(t: number, p1: number, p2: number): number {
  const u = 1 - t
  return 3 * u * u * p1 + 6 * u * t * (p2 - p1) + 3 * t * t * (1 - p2)
}

/** True when a key's handles mean "hold the previous value until this key" (y = 999 on both). */
export function isSnap(h1: V2, h2: V2): boolean {
  return h1.y === 999 && h2.y === 999
}

/**
 * Value between `a` (at current = 0) and `b` (at current = max) following the destination key's
 * handles `h1` (P1) and `h2` (P2). Report 01 §6.3.1, in this exact order: snap is checked before
 * the end test (a snap segment returns `a` even at current ≥ max — the destination key's own
 * segment then writes `b`), then `max == 0 || current >= max` returns `b`, then Newton–Raphson ×5
 * on x(t) = current/max with t clamped to [0,1] and an early exit when |x'| < 1e-5.
 */
export function interpolate(current: number, max: number, a: number, b: number, h1: V2, h2: V2): number {
  if (h1.y === 999 && h2.y === 999) return a
  if (max === 0 || current >= max) return b
  const x0 = current / max
  let t = x0
  for (let i = 0; i < 5; i++) {
    const x = bezierAt(t, h1.x, h2.x)
    const dx = bezierDeriv(t, h1.x, h2.x)
    if (Math.abs(dx) < 1e-5) break
    t = clamp(t - (x - x0) / dx, 0, 1)
  }
  return a + (b - a) * bezierAt(t, h1.y, h2.y)
}

const PRESET_EPS = 1e-3

function near(a: V2, b: V2): boolean {
  return Math.abs(a.x - b.x) <= PRESET_EPS && Math.abs(a.y - b.y) <= PRESET_EPS
}

/**
 * Name the handle pair. Any pair with P1.x = P1.y and P2.x = P2.y is an exactly linear curve
 * (x(t) = y(t)), which covers both the editor's (1/3,1/3)/(2/3,2/3) and the (0,0)/(0,0) the
 * samples carry on frame-0 keys, so both read as `Linear`. Unknown pairs are `Custom`.
 */
export function presetOf(h1: V2, h2: V2): HandlePreset {
  if (isSnap(h1, h2)) return 'Snap'
  if (Math.abs(h1.x - h1.y) <= PRESET_EPS && Math.abs(h2.x - h2.y) <= PRESET_EPS) return 'Linear'
  for (const name of ['SineIn', 'SineOut', 'SineInOut'] as const) {
    const [p1, p2] = HANDLES[name]
    if (near(h1, p1) && near(h2, p2)) return name
  }
  return 'Custom'
}

/** The handle pair for a named preset (fresh objects, safe to store); `Custom` falls back to Linear. */
export function handlesOf(preset: HandlePreset | string): [V2, V2] {
  const pair = (HANDLES as Record<string, readonly [V2, V2] | undefined>)[preset] ?? HANDLES.Linear
  return [{ ...pair[0] }, { ...pair[1] }]
}

/** Loose numeric equality for tests and gizmo snapping. */
export function approxEq(a: number, b: number, eps = 1e-9): boolean {
  return Math.abs(a - b) <= eps
}
